import { action, computed, observable, reaction, toJS } from 'mobx'
import { cloneDeep, debounce, isFunction, mapValues, merge, omit, pick } from 'lodash'

import GenericStoreCoordinatorService from 'Services/genericStoreCoordinator/Service'
import StoreInitializers from 'Stores/StoreInitializers'
import { filterSortMerge } from 'Stores/StoreInstances'

// import { isArray } from 'util';

export default class GenericStoreCoordinatorInstance {
  @observable _filter = {}
  @observable _sortOrder = []
  @observable _mergeFields = {}
  _transform = {}

  _master = {
    instance: undefined,
    masterFilterTransform: f => { return omit(f, this._master.slaveControlledFields) },
    slaveFilterTransform: f => { return pick(f, this._master.slaveControlledFields) },
    masterMergeFieldsTransform: m => { return m },
    slaveMergeFieldsTransform: m => { return m },
  }

  _slaveInstance = undefined

  _d = {
    enabled: false,
  }

  constructor (uuid, storeType, instanceOpts, referenceName = undefined) {
    this.uuid = uuid
    this.type = storeType
    this.referenceName = referenceName
    this.filter = instanceOpts.defaultFilter || {}
    this.dataStore = StoreInitializers(this.type)(this.filter, instanceOpts.storeOpts)
    this._instanceOpts = cloneDeep(instanceOpts)
    GenericStoreCoordinatorService._registerChildInstance(this)

    if (instanceOpts.slaveTo) {
      this._master = {
        ...this._master,
        instance: GenericStoreCoordinatorService.getById(instanceOpts.slaveTo.uuid),
        slaveControlledFields: instanceOpts.slaveTo.slaveControlledFields,
        ...instanceOpts.slaveTo.masterFilterTransform && { masterFilterTransform: instanceOpts.slaveTo.masterFilterTransform.bind(this) },
        ...instanceOpts.slaveTo.slaveFilterTransform && { slaveFilterTransform: instanceOpts.slaveTo.slaveFilterTransform.bind(this) },
        ...instanceOpts.slaveTo.masterMergeFieldsTransform && { masterMergeFieldsTransform: instanceOpts.slaveTo.masterMergeFieldsTransform.bind(this) },
        ...instanceOpts.slaveTo.slaveMergeFieldsTransform && { slaveMergeFieldsTransform: instanceOpts.slaveTo.slaveMergeFieldsTransform.bind(this) },
      }

      this._master.instance.slaveInstance = this

      reaction(
        () => this._master.instance.filter,
        () => {
          const f = {
            ...this._master.slaveFilterTransform(this.filter),
            ...this._master.masterFilterTransform(this._master.instance.filter),
          }
          this.__debugConsole('Master Instance Filter Changed', this._master.instance.filter)
          this.__debugConsole(' - - -', this._master.slaveFilterTransform(this.filter), this._master.masterFilterTransform(this._master.instance.filter), f)
          this.filter = f
        }
      )

      reaction(
        () => this._master.instance.mergeFields,
        () => {
          const m = {
            ...this._master.slaveMergeFieldsTransform(this.mergeFields),
            ...this._master.masterMergeFieldsTransform(this._master.instance.mergeFields),
          }
          this.__debugConsole('Master Instance Merge Fields Changed', this._master.instance.mergeFields)
          this.__debugConsole(' - - -', this._master.slaveMergeFieldsTransform(this.mergeFields), this._master.masterMergeFieldsTransform(this._master.instance.mergeFields), m)
          this.mergeFields = m
        }
      )
    }

    reaction(
      () => this.mergedFilter,
      debounce(() => {
        this.__debugConsole('@ Changing Official Filter', toJS(this.mergedFilter))
        this.dataStore.setFilter(toJS(this.mergedFilter))
        this.dataStore.initFetch()
      }, 100)
    )
  }

  @action
  setFilterProperty (key, value) {
    this.filter = merge({}, this.filter, { [key]: value })
  }

  set filter (f) {
    this._filter = f
  }

  @computed
  get filter () {
    return toJS(this._filter)
  }

  @computed
  get mergedFilter () {
    return filterSortMerge(
      this.transformFilter(
        merge({}, this.filter, this.mergeFields)
      ),
      toJS(this.sortOrder)
    )
  }

  @computed
  get filterWithoutPage () {
    return omit(this.mergedFilter, 'page')
  }

  set mergeFields (mf) {
    this._mergeFields = mf
  }

  @computed
  get mergeFields () {
    return toJS(this._mergeFields)
  }

  set queryTransform (trans) {
    this._transform = trans
  }

  @computed
  get queryTransform () {
    return this._transform
  }

  set sortOrder (sortOrder) {
    this._sortOrder = sortOrder
  }

  @computed
  get sortOrder () {
    return this._sortOrder
  }

  @computed
  get records () {
    return this.dataStore.records
  }

  @computed
  get allRecords () {
    return this.dataStore.allRecords
  }

  @computed
  get paginationData () {
    return this.dataStore.links.pagination
  }

  @computed
  get loadingData () {
    return this.dataStore.loading
  }

  set slaveInstance (slaveInstance) {
    this._slaveInstance = slaveInstance
  }

  get slaveInstance () {
    return this._slaveInstance
  }

  get hasSlave () {
    return !!this._slaveInstance
  }

  @action
  reloadDataStore () {
    const reloadPromise = this.dataStore.reload()
    if (this.hasSlave) {
      this.slaveInstance.reloadDataStore()
    }

    return reloadPromise
  }

  @action
  goToPage (pageNumOrString) {
    // console.log('coord#goToPage', pageNumOrString)
    if (pageNumOrString === 'prev') {
      this.dataStore.goToPreviousPage()
    } else if (pageNumOrString === 'next') {
      this.dataStore.goToNextPage()
    } else if (pageNumOrString === 'last') {
      this.dataStore.goToLastPage()
    } else if (pageNumOrString === 'first') {
      this.dataStore.goToFirstPage()
    } else {
      this.dataStore.goToPage(pageNumOrString)
    }
  }

  @computed
  get currentPage () {
    return (
      this.dataStore.links.pagination &&
      this.dataStore.links.pagination.current_page
    )
  }

  transformFilter (f) {
    return mapValues(f, (val, key) => {
      if (this.queryTransform[key]) {
        if (isFunction(this.queryTransform[key])) {
          return this.queryTransform[key](val)
        } else {
          console.warn(
            '[GenericStoreCoordinator:Instance] Key transform must be a function.'
          )
          return val
        }
      } else {
        return val
      }
    })
  }

  // Debug Methods

  set debug (d) {
    this._d.enabled = d
  }

  __debugConsole () {
    if (this._d.enabled) {
      /* eslint-disable no-console */
      console.log(`[GenericStoreCoordinatorInstance]:`, ...arguments)
      /* eslint-enable no-console */
    }
  }
}
